import type { DreAllViewportTypes } from 'config/constants';
import { currentStackAtomFamily } from 'domains/viewer/Viewer/viewerRecoilContext';
import { useImagingContext } from 'domains/viewer/ViewportDre/modules/imaging/ImagingContext';
import { calculateDefaultWindowLevel } from 'domains/viewer/ViewportDre/modules/imaging/utils';
import type {
  Stack as FrameStack,
  WindowLevelDisplayState,
} from 'domains/viewer/ViewportsConfigurations';
import {
  DEFAULT_WINDOW_LEVEL_STATE,
  windowLevelDisplaySelector,
} from 'domains/viewer/ViewportsConfigurations';
import { useGuideWindowLevelSelector } from 'domains/viewer/ViewportsConfigurations/windowLevelState';
import type { WithApolloContext } from 'modules/Apollo/types';
import { useMemo } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import type { BaseImagingProvider } from './imaging/BaseImagingProvider';
import { useViewportId, useViewType } from './state';

// Fallback values are used when ImagingProvider isn't loaded/ready yet
export const DEFAULT_WINDOW_FALLBACK = 1;
export const DEFAULT_LEVEL_FALLBACK = 0;

type SetWindowLevel = (arg: { window: number; level: number }) => void;

/**
 * Consolidated hook for reading and writing window level state.
 * The returned `windowLevel` has provider defaults baked in as fallbacks,
 * so consumers don't need to resolve defaults themselves.
 */
export function useWindowLevel(imagingProvider: Nullable<BaseImagingProvider<FrameStack>>): {
  windowLevel: Nullable<WindowLevelDisplayState>;
  setWindowLevel: SetWindowLevel;
  setLayeredWindowLevel: SetWindowLevel;
} {
  const viewportId = useViewportId();
  const viewType = useViewType();

  const windowLevelCacheKey = imagingProvider?.getWindowLevelCacheKey({
    viewType,
    viewportId,
  });

  const cachedWindowLevel = useRecoilValue(windowLevelDisplaySelector(windowLevelCacheKey));

  const updateWindowLevelState = useRecoilCallback(
    ({ set }) =>
      (patch: Partial<WindowLevelDisplayState>) => {
        const cacheKey = imagingProvider?.getWindowLevelCacheKey({ viewType, viewportId });
        if (cacheKey == null) return;
        set(windowLevelDisplaySelector(cacheKey), (currentWL) => ({
          ...(currentWL ?? DEFAULT_WINDOW_LEVEL_STATE),
          ...patch,
        }));
      },
    [viewportId, viewType, imagingProvider]
  );

  const setWindowLevel = useRecoilCallback(
    () =>
      ({ window, level }: { window: number; level: number }) => {
        updateWindowLevelState({ window, level });
      },
    [updateWindowLevelState]
  );

  const setLayeredWindowLevel = useRecoilCallback(
    () =>
      ({ window, level }: { window: number; level: number }) => {
        updateWindowLevelState({ windowLayered: window, levelLayered: level });
      },
    [updateWindowLevelState]
  );

  const activeSlice = imagingProvider?.getActiveSlice(viewType, viewportId) ?? 0;

  const windowLevel = useMemo((): Nullable<WindowLevelDisplayState> => {
    const isLayered = imagingProvider?.isLayeredStack();
    const defaultWindowLevel = isLayered
      ? imagingProvider?.getLayerProvider(0).getWindowLevelForSlice(activeSlice, viewType)
      : imagingProvider?.getWindowLevelForSlice(activeSlice, viewType);

    if (!defaultWindowLevel && !cachedWindowLevel) return null;

    const result: WindowLevelDisplayState = {
      window: cachedWindowLevel?.window ?? defaultWindowLevel?.windowWidth,
      level: cachedWindowLevel?.level ?? defaultWindowLevel?.windowCenter,
      window3d: cachedWindowLevel?.window3d,
      level3d: cachedWindowLevel?.level3d,
      windowLayered: cachedWindowLevel?.windowLayered,
      levelLayered: cachedWindowLevel?.levelLayered,
    };

    if (isLayered) {
      const layered = imagingProvider
        ?.getLayerProvider(1)
        .getWindowLevelForSlice(activeSlice, viewType);
      result.windowLayered = cachedWindowLevel?.windowLayered ?? layered?.windowWidth;
      result.levelLayered = cachedWindowLevel?.levelLayered ?? layered?.windowCenter;
    }

    return result;
  }, [cachedWindowLevel, imagingProvider, activeSlice, viewType]);

  return { windowLevel, setWindowLevel, setLayeredWindowLevel };
}

export const useSetGuideWindowLevel = ({
  context,
}: WithApolloContext): ((window: number, level: number) => void) => {
  const guideWindowLevelSelector = useGuideWindowLevelSelector({ context });

  return useRecoilCallback(
    ({ snapshot, set }) =>
      async (window, level) => {
        const currentWL = await snapshot.getPromise(guideWindowLevelSelector);
        set(guideWindowLevelSelector, {
          ...(currentWL ?? DEFAULT_WINDOW_LEVEL_STATE),
          window,
          level,
        });
      },
    [guideWindowLevelSelector]
  );
};

function getBaseWindowLevel(
  imagingProvider: NonNullable<ReturnType<typeof useImagingContext>['imagingProvider']>,
  windowLevelState: Nullable<WindowLevelDisplayState>,
  activeSlice: number,
  viewType: DreAllViewportTypes,
  reset: boolean
): { window: number; level: number } {
  if (!reset && windowLevelState?.window != null && windowLevelState?.level != null) {
    return { window: windowLevelState.window, level: windowLevelState.level };
  }
  const { windowWidth, windowCenter } = imagingProvider.getWindowLevelForSlice(
    activeSlice,
    viewType
  );
  return { window: windowWidth, level: windowCenter };
}

function getLayeredWindowLevel(
  imagingProvider: NonNullable<ReturnType<typeof useImagingContext>['imagingProvider']>,
  windowLevelState: Nullable<WindowLevelDisplayState>,
  layeredStackSmid: string,
  reset: boolean
): { window: number; level: number } {
  const layeredProvider = imagingProvider.getRefProviderByStackSmid(layeredStackSmid);
  // Assumes a single overlay layer @ idx 1. If we extend to n > 1 layers in the future,
  // we'll need to modify this function to loop over all the layers
  const defaults = calculateDefaultWindowLevel(layeredProvider?.getRange());

  const hasCachedLayered =
    windowLevelState?.windowLayered != null &&
    windowLevelState?.levelLayered != null &&
    !(
      windowLevelState.windowLayered === DEFAULT_WINDOW_LEVEL_STATE.windowLayered &&
      windowLevelState.levelLayered === DEFAULT_WINDOW_LEVEL_STATE.levelLayered
    );

  const level = reset
    ? layeredProvider?.imageParams.colorLevel
    : hasCachedLayered
      ? windowLevelState!.levelLayered
      : layeredProvider?.imageParams.colorLevel;
  const window = reset
    ? layeredProvider?.imageParams.colorWindow
    : hasCachedLayered
      ? windowLevelState!.windowLayered
      : layeredProvider?.imageParams.colorWindow;

  return {
    window: window ?? defaults.window,
    level: level ?? defaults.level,
  };
}

export function useInitWindowLevel(viewportId: string): (
  options?: Nullable<{
    reset?: boolean;
  }>
) => void {
  const viewType = useViewType();
  const { imagingProvider } = useImagingContext();
  const { setWindowLevel, setLayeredWindowLevel } = useWindowLevel(imagingProvider);

  return useRecoilCallback(
    ({ snapshot }) =>
      (
        options?: Nullable<{
          reset?: boolean;
        }>
      ) => {
        if (imagingProvider == null) return;

        const reset = options?.reset ?? false;
        const activeSlice = imagingProvider.getActiveSlice(viewType, viewportId) ?? 0;
        const windowLevelCacheKey = imagingProvider.getWindowLevelCacheKey({
          viewType,
          viewportId,
        });

        const windowLevelState = snapshot
          .getLoadable(windowLevelDisplaySelector(windowLevelCacheKey))
          .getValue();

        setWindowLevel(
          getBaseWindowLevel(imagingProvider, windowLevelState, activeSlice, viewType, reset)
        );

        const stack = snapshot.getLoadable(currentStackAtomFamily(viewportId)).getValue();
        const layeredStackSmid =
          stack != null && 'stackLayers' in stack ? stack.stackLayers?.[1].stackSmid : null;

        if (imagingProvider.isLayeredStack() && layeredStackSmid != null) {
          setLayeredWindowLevel(
            getLayeredWindowLevel(imagingProvider, windowLevelState, layeredStackSmid, reset)
          );
        }
      },
    [viewportId, viewType, imagingProvider, setWindowLevel, setLayeredWindowLevel]
  );
}
